import api from "./api";
import { getApiUrl } from "./config";
import { handleApiCall } from "../utils/apiResponseHandler";

// Audit Logs
export interface AuditLog {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  action: string;
  entityType: string;
  entityId?: string;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuditStatsResponse {
  totalActions: number;
  actionsByType: Record<string, number>;
  actionsByUser: Record<string, number>;
  recentActions: AuditLog[];
}

// User Analytics
export interface UserAnalytics {
  registrations: {
    daily: { date: string; count: number }[];
    weekly: { week: string; count: number }[];
    monthly: { month: string; count: number }[];
  };
  userTypes: { type: string; count: number; percentage: number }[];
  accountTypes?: { type: string; count: number; percentage: number }[];
  activeUsers: { total: number; active: number; inactive: number; activePercentage: number };
  trialConversion: {
    totalTrials: number;
    converted: number;
    expired: number;
    active: number;
    activeTrialStatus?: number;
    conversionRate: number;
  };
  growth: { weekOverWeek: number; monthOverMonth: number; yearOverYear: number };
}

// Suspended/Deleted Users
export interface SuspendedUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  userType: string;
  suspendedAt: string;
  suspendedBy: string;
  reason: string;
  duration: string;
  autoReactivate: boolean;
  reactivateDate?: string;
}

export interface DeletedUser {
  id: string;
  name: string;
  email: string;
  userType: string;
  deletedAt: string;
  deletedBy: string;
  reason: string;
  dataRetentionDays: number;
  permanentDeleteDate: string;
  canRecover: boolean;
}

export interface DeactivationRequest {
  id: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  reason: string | null;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  result: Record<string, any> | null;
  target_user_id: string;
  target_name: string;
  target_email: string;
  target_account_status: string;
  target_primary_agency: string | null;
  requested_by_user_id: string;
  requested_by_name: string;
  requested_by_email: string;
  agency_id: string;
  agency_name: string;
  reviewed_by_name: string | null;
}

// Calling credits (All Users → Calling credits)
export interface AdminDialCreditRow {
  id: string;
  type: "purchase" | "usage" | "refund" | "grant" | "promo" | "adjustment";
  amountCents: number;
  balanceAfterCents: number;
  description?: string;
  createdAt: string;
  /** A Direct130 dialing charge not yet given back. */
  canCreditBack: boolean;
  creditedBack: boolean;
}

export interface AdminDialCredits {
  balanceCents: number;
  /** null unless a Direct130 agent. */
  direct130Dialing: { metered: boolean; issuedPremium30d: number; threshold: number } | null;
  items: AdminDialCreditRow[];
}

export interface AdminCreditBackResult {
  success: boolean;
  newBalanceCents: number;
  amountChargedCents?: number;
  alreadyApplied?: boolean;
  reason?: string;
}

class AdminService {
  // ============ AUDIT LOGS ============
  async getAuditLogs(params: {
    action?: string;
    entityType?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<AuditLogsResponse> {
    const queryParams = new URLSearchParams();
    if (params.action) queryParams.append("action", params.action);
    if (params.entityType) queryParams.append("entityType", params.entityType);
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());

    const response = await handleApiCall<AuditLogsResponse>(async () => {
      return await api.get({
        url: getApiUrl(`/admin/audit-log?${queryParams.toString()}`),
        auth: true,
      });
    });

    return response;
  }

  async getAuditStats(params: { startDate?: string; endDate?: string }): Promise<AuditStatsResponse> {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);

    const response = await handleApiCall<AuditStatsResponse>(async () => {
      return await api.get({
        url: getApiUrl(`/admin/audit-log/stats?${queryParams.toString()}`),
        auth: true,
      });
    });

    return response;
  }

  async exportAuditLogs(params: {
    action?: string;
    entityType?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ csv: string; filename: string }> {
    const queryParams = new URLSearchParams();
    if (params.action) queryParams.append("action", params.action);
    if (params.entityType) queryParams.append("entityType", params.entityType);
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);

    const response = await handleApiCall<{ csv: string; filename: string }>(async () => {
      return await api.get({
        url: getApiUrl(`/admin/audit-log/export?${queryParams.toString()}`),
        auth: true,
      });
    });

    return response;
  }

  async createAuditLog(data: {
    action: string;
    entityType: string;
    entityId?: string;
    oldValues?: Record<string, any>;
    newValues?: Record<string, any>;
    metadata?: Record<string, any>;
  }): Promise<AuditLog> {
    const response = await handleApiCall<AuditLog>(async () => {
      return await api.post({
        url: getApiUrl("/admin/audit-log"),
        body: data,
        auth: true,
      });
    });

    return response;
  }

  // ============ USER ANALYTICS ============
  async getUserAnalytics(): Promise<UserAnalytics> {
    const response = await handleApiCall<UserAnalytics>(async () => {
      return await api.get({
        url: getApiUrl("/admin/analytics/users"),
        auth: true,
      });
    });

    return response;
  }

  // ============ USER MANAGEMENT ============
  async getSuspendedUsers(params: { page?: number; limit?: number }): Promise<{
    users: SuspendedUser[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());

    const response = await handleApiCall<{
      users: SuspendedUser[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(async () => {
      return await api.get({
        url: getApiUrl(`/admin/users/suspended?${queryParams.toString()}`),
        auth: true,
      });
    });

    return response;
  }

  async getDeactivationRequests(status: "pending" | "approved" | "rejected" | "cancelled" | "all" = "pending"): Promise<{ requests: DeactivationRequest[]; total: number }> {
    const response = await handleApiCall<{ data: { requests: DeactivationRequest[]; total: number } }>(async () =>
      api.get({ url: getApiUrl(`/admin/users/deactivation-requests?status=${status}`), auth: true }),
    );
    return (response as any)?.data ?? response;
  }

  async approveDeactivationRequest(id: string, note?: string): Promise<any> {
    return handleApiCall(async () =>
      api.post({ url: getApiUrl(`/admin/users/deactivation-requests/${id}/approve`), body: { note }, auth: true }),
    );
  }

  async rejectDeactivationRequest(id: string, note?: string): Promise<any> {
    return handleApiCall(async () =>
      api.post({ url: getApiUrl(`/admin/users/deactivation-requests/${id}/reject`), body: { note }, auth: true }),
    );
  }

  async reactivateUser(userId: string, reason?: string): Promise<any> {
    const response = await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl(`/admin/users/${userId}/reactivate`),
        body: { reason },
        auth: true,
      });
    });

    return response;
  }

  async extendSuspension(userId: string, days: number, reason?: string): Promise<any> {
    const response = await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl(`/admin/users/${userId}/extend-suspension`),
        body: { days, reason },
        auth: true,
      });
    });

    return response;
  }

  async suspendUser(userId: string, reason: string, durationDays?: number): Promise<any> {
    const response = await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl(`/admin/users/${userId}/suspend`),
        body: { reason, durationDays },
        auth: true,
      });
    });

    return response;
  }

  async permanentSuspend(userId: string, reason: string): Promise<any> {
    const response = await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl(`/admin/users/${userId}/permanent-suspend`),
        body: { reason },
        auth: true,
      });
    });

    return response;
  }

  async getDeletedUsers(params: { page?: number; limit?: number }): Promise<{
    users: DeletedUser[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());

    const response = await handleApiCall<{
      users: DeletedUser[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(async () => {
      return await api.get({
        url: getApiUrl(`/admin/users/deleted?${queryParams.toString()}`),
        auth: true,
      });
    });

    return response;
  }

  async recoverDeletedUser(userId: string): Promise<any> {
    const response = await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl(`/admin/users/${userId}/recover`),
        body: {},
        auth: true,
      });
    });

    return response;
  }

  async permanentDeleteUser(userId: string): Promise<any> {
    const response = await handleApiCall(async () => {
      return await api.delete({
        url: getApiUrl(`/admin/users/${userId}/permanent-delete`),
        auth: true,
      });
    });

    return response;
  }

  // ============ BULK OPERATIONS ============
  async bulkUpdateAccountType(userIds: string[], accountType: string): Promise<{
    success: number;
    failed: number;
    errors: string[];
  }> {
    const response = await handleApiCall<{ success: number; failed: number; errors: string[] }>(async () => {
      return await api.post({
        url: getApiUrl("/admin/users/bulk/update-account-type"),
        body: { userIds, accountType },
        auth: true,
      });
    });

    return response;
  }

  async bulkUpdateStatus(userIds: string[], status: string, reason?: string): Promise<{
    success: number;
    failed: number;
    errors: string[];
  }> {
    const response = await handleApiCall<{ success: number; failed: number; errors: string[] }>(async () => {
      return await api.post({
        url: getApiUrl("/admin/users/bulk/update-status"),
        body: { userIds, status, reason },
        auth: true,
      });
    });

    return response;
  }

  async bulkSendEmail(userIds: string[], subject: string, body: string): Promise<{
    success: number;
    failed: number;
    errors: string[];
  }> {
    const response = await handleApiCall<{ success: number; failed: number; errors: string[] }>(async () => {
      return await api.post({
        url: getApiUrl("/admin/users/bulk/send-email"),
        body: { userIds, subject, body },
        auth: true,
      });
    });

    return response;
  }

  async bulkExportUsers(userIds: string[], format: string, fields: string[]): Promise<{ data: string; filename: string }> {
    const response = await handleApiCall<{ data: string; filename: string }>(async () => {
      return await api.post({
        url: getApiUrl("/admin/users/bulk/export"),
        body: { userIds, format, fields },
        auth: true,
      });
    });

    return response;
  }

  // ============ AGENCY MANAGEMENT ============
  async getAgencyOverview(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/agencies/overview"),
        auth: true,
      });
    });
  }

  async getAgencyHierarchy(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/agencies/hierarchy"),
        auth: true,
      });
    });
  }

  async getAgencySubscriptions(params: { status?: string; plan?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append("status", params.status);
    if (params.plan) queryParams.append("plan", params.plan);

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/agencies/subscriptions?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async cancelAgencySubscription(id: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl(`/admin/agencies/subscriptions/${id}/cancel`),
        body: {},
        auth: true,
      });
    });
  }

  async reactivateAgencySubscription(id: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl(`/admin/agencies/subscriptions/${id}/reactivate`),
        body: {},
        auth: true,
      });
    });
  }

  async getAgencyPerformance(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/agencies/performance"),
        auth: true,
      });
    });
  }

  async updateAgencySettings(id: string, settings: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.put({
        url: getApiUrl(`/admin/agencies/${id}/settings`),
        body: settings,
        auth: true,
      });
    });
  }

  async createAgency(agencyData: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/agencies/create"),
        body: agencyData,
        auth: true,
      });
    });
  }

  // ============ FINANCIAL MANAGEMENT ============
  async getRevenueDashboard(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/financial/revenue/dashboard"),
        auth: true,
      });
    });
  }

  async getTransactions(params: { page?: number; limit?: number; status?: string; startDate?: string; endDate?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.status) queryParams.append("status", params.status);
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/financial/transactions?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async getSubscriptions(params: { status?: string; plan?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append("status", params.status);
    if (params.plan) queryParams.append("plan", params.plan);

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/financial/subscriptions?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async getRefunds(params: { page?: number; limit?: number }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/financial/refunds?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async processRefund(transactionId: string, amount: number, reason: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/financial/refunds/process"),
        body: { transactionId, amount, reason },
        auth: true,
      });
    });
  }

  async getPricingPlans(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/financial/pricing-plans"),
        auth: true,
      });
    });
  }

  async updatePricingPlan(id: string, planData: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.put({
        url: getApiUrl(`/admin/financial/pricing-plans/${id}`),
        body: planData,
        auth: true,
      });
    });
  }

  async getCoupons(active?: boolean): Promise<any> {
    const queryParams = new URLSearchParams();
    if (active !== undefined) queryParams.append("active", active.toString());

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/financial/coupons?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async createCoupon(couponData: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/financial/coupons/create"),
        body: couponData,
        auth: true,
      });
    });
  }

  async getRevenueReport(startDate: string, endDate: string): Promise<any> {
    const queryParams = new URLSearchParams();
    queryParams.append("startDate", startDate);
    queryParams.append("endDate", endDate);

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/financial/reports/revenue?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  // ============ COMMUNICATIONS ============
  async getCalls(params: any): Promise<any> {
    // Filter out undefined/null/empty string values before creating URLSearchParams
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const queryParams = new URLSearchParams(filteredParams as Record<string, string>);
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/communications/calls?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async getSmsHistory(params: any): Promise<any> {
    // Filter out undefined/null/empty string values before creating URLSearchParams
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const queryParams = new URLSearchParams(filteredParams as Record<string, string>);
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/communications/sms?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async getFaxHistory(params: any): Promise<any> {
    // Filter out undefined/null/empty string values before creating URLSearchParams
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const queryParams = new URLSearchParams(filteredParams as Record<string, string>);
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/communications/fax?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async getCommunicationCosts(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/communications/costs"),
        auth: true,
      });
    });
  }

  async createBroadcast(broadcastData: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/communications/broadcast"),
        body: broadcastData,
        auth: true,
      });
    });
  }

  async getBroadcastHistory(params: any): Promise<any> {
    const queryParams = new URLSearchParams(params);
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/communications/broadcast/history?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  // ============ SYSTEM CONFIGURATION ============
  async getEnvironmentVars(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/system/environment-vars"),
        auth: true,
      });
    });
  }

  async updateEnvironmentVars(vars: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.put({
        url: getApiUrl("/admin/system/environment-vars"),
        body: vars,
        auth: true,
      });
    });
  }

  async getFeatureFlags(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/system/feature-flags"),
        auth: true,
      });
    });
  }

  async toggleFeatureFlag(id: string, enabled: boolean): Promise<any> {
    return await handleApiCall(async () => {
      return await api.put({
        url: getApiUrl(`/admin/system/feature-flags/${id}`),
        body: { enabled },
        auth: true,
      });
    });
  }

  async getRateLimits(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/system/rate-limits"),
        auth: true,
      });
    });
  }

  async updateRateLimits(limits: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.put({
        url: getApiUrl("/admin/system/rate-limits"),
        body: limits,
        auth: true,
      });
    });
  }

  async getEmailTemplates(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/system/email-templates"),
        auth: true,
      });
    });
  }

  async updateEmailTemplate(id: string, template: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.put({
        url: getApiUrl(`/admin/system/email-templates/${id}`),
        body: template,
        auth: true,
      });
    });
  }

  async toggleMaintenanceMode(enabled: boolean): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/system/maintenance-mode"),
        body: { enabled },
        auth: true,
      });
    });
  }

  async clearCache(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/system/cache/clear"),
        body: {},
        auth: true,
      });
    });
  }

  async triggerBackup(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/system/backup/trigger"),
        body: {},
        auth: true,
      });
    });
  }

  // ============ MONITORING ============
  async getSystemHealth(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/system/health"),
        auth: true,
      });
    });
  }

  async getApiPerformance(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/monitoring/api-performance"),
        auth: true,
      });
    });
  }

  async getErrors(params: any): Promise<any> {
    const queryParams = new URLSearchParams(params);
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/monitoring/errors?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async getQueueStatus(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/monitoring/queues"),
        auth: true,
      });
    });
  }

  async getAlertRules(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/monitoring/alerts"),
        auth: true,
      });
    });
  }

  async createAlertRule(ruleData: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/monitoring/alerts/create"),
        body: ruleData,
        auth: true,
      });
    });
  }

  async updateAlertRule(id: string, ruleData: any): Promise<any> {
    return await handleApiCall(async () => {
      return await api.put({
        url: getApiUrl(`/admin/monitoring/alerts/${id}`),
        body: ruleData,
        auth: true,
      });
    });
  }

  // ============ SECURITY ============
  async getActiveSessions(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/security/active-sessions"),
        auth: true,
      });
    });
  }

  async getUserPresence(params: {
    page?: number;
    limit?: number;
    onlineOnly?: boolean;
    search?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.onlineOnly !== undefined) queryParams.append("onlineOnly", params.onlineOnly.toString());
    if (params.search) queryParams.append("search", params.search);

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/security/user-presence?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async terminateSession(sessionId: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.delete({
        url: getApiUrl(`/admin/security/sessions/${sessionId}`),
        auth: true,
      });
    });
  }

  async getFailedLogins(params: any): Promise<any> {
    const queryParams = new URLSearchParams(params);
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/security/failed-logins?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  async getIpBlocklist(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/security/ip-blocklist"),
        auth: true,
      });
    });
  }

  async blockIp(ip: string, reason: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/security/ip-blocklist"),
        body: { ip, reason },
        auth: true,
      });
    });
  }

  async unblockIp(ip: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.delete({
        url: getApiUrl(`/admin/security/ip-blocklist/${ip}`),
        auth: true,
      });
    });
  }

  async getApiKeys(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/security/api-keys"),
        auth: true,
      });
    });
  }

  async generateApiKey(name: string, permissions: string[]): Promise<any> {
    return await handleApiCall(async () => {
      return await api.post({
        url: getApiUrl("/admin/security/api-keys/generate"),
        body: { name, permissions },
        auth: true,
      });
    });
  }

  async revokeApiKey(id: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.delete({
        url: getApiUrl(`/admin/security/api-keys/${id}`),
        auth: true,
      });
    });
  }

  async getComplianceReports(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/security/compliance-reports"),
        auth: true,
      });
    });
  }

  async getSecurityAudit(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin/security/audit"),
        auth: true,
      });
    });
  }

  // ============ DASHBOARD ============
  async getDashboardOverview(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin-panel/dashboard/overview"),
        auth: true,
      });
    });
  }

  // ============ DOCUMENT REQUESTS ============
  async getDocumentRequests(params?: {
    status?: string;
    carrierId?: string;
    priority?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.carrierId) queryParams.append("carrierId", params.carrierId);
    if (params?.priority) queryParams.append("priority", params.priority);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/document-requests${queryString}`),
        auth: true,
      });
    });
  }

  async getDocumentRequestById(id: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin/document-requests/${id}`),
        auth: true,
      });
    });
  }

  async updateDocumentRequest(
    id: string,
    data: { status?: string; adminNotes?: string }
  ): Promise<any> {
    return await handleApiCall(async () => {
      return await api.patch({
        url: getApiUrl(`/admin/document-requests/${id}`),
        body: data,
        auth: true,
      });
    });
  }

  // ============ SESSION HISTORY & ACTIVITY ============
  /**
   * Get session history and activity analytics
   * @param userId - Optional user ID to filter by specific user
   * @param limit - Number of sessions to return (default 50)
   * @param offset - Pagination offset
   */
  async getSessionHistory(params: {
    userId?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.offset) queryParams.append("offset", params.offset.toString());

    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin-panel/sessions/history?${queryParams.toString()}`),
        auth: true,
      });
    });
  }

  /**
   * Get detailed session history for a specific user
   * Includes login patterns, session durations, and activity timeline
   */
  async getUserSessionDetails(userId: string): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl(`/admin-panel/sessions/user/${userId}`),
        auth: true,
      });
    });
  }

  /**
   * Get real-time session activity stats
   * Used for the live dashboard widget
   */
  async getRealtimeSessionStats(): Promise<any> {
    return await handleApiCall(async () => {
      return await api.get({
        url: getApiUrl("/admin-panel/sessions/realtime-stats"),
        auth: true,
      });
    });
  }

  async getUserDialCredits(userId: string): Promise<AdminDialCredits> {
    return handleApiCall<AdminDialCredits>(() =>
      api.get({ url: getApiUrl(`/dial-credits/admin/users/${userId}`), auth: true }),
    );
  }

  /** { transactionId } gives one dialing charge back; { amountCents, requestId } adds credits. */
  async creditBackDialCredits(
    userId: string,
    body: ({ transactionId: string } | { amountCents: number; requestId: string }) & { reason: string },
  ): Promise<AdminCreditBackResult> {
    return handleApiCall<AdminCreditBackResult>(() =>
      api.post({ url: getApiUrl(`/dial-credits/admin/users/${userId}/credit-back`), body, auth: true }),
    );
  }
}

export const adminService = new AdminService();
